/**
 * Centralized redirect map for all route redirects
 * Single source of truth for route redirects across the application
 */

export interface RedirectRule {
  from: string;
  to: string;
  replace?: boolean;
}

export const redirects: RedirectRule[] = [
  // Admin dashboard redirects to Command Center
  { from: '/admin-dashboard', to: '/admin/command-center', replace: true },
  { from: '/admin', to: '/admin/command-center', replace: true },
  { from: '/admin/dashboard', to: '/admin/command-center', replace: true },
  { from: '/admin/advanced', to: '/admin/command-center', replace: true },
  
  // User management redirects
  { from: '/admin/users', to: '/admin/command-center/users', replace: true },
  { from: '/admin/user-management', to: '/admin/command-center/users', replace: true },
  
  // Database monitoring redirects
  { from: '/admin/database-io-monitor', to: '/admin/command-center/platform-health', replace: true },
  
  // Patient management redirects
  { from: '/admin/patient-management', to: '/admin/command-center?tab=pipeline', replace: true },
  { from: '/admin/bulk-patients', to: '/admin/command-center?tab=pipeline', replace: true },
  { from: '/admin/patient-activation', to: '/admin/command-center?tab=pipeline', replace: true },
  { from: '/admin/analytics', to: '/admin/command-center?tab=pipeline', replace: true },
  { from: '/admin/trials', to: '/admin/command-center/autopilot', replace: true },
  { from: '/admin/command-center/trials', to: '/admin/command-center/autopilot', replace: true },
  
  // CRO and Sponsor dashboard redirects
  { from: '/cro-dashboard', to: '/cro/command-center', replace: true },
  { from: '/sponsor-dashboard', to: '/sponsor/command-center', replace: true },
  { from: '/sponsor-analytics', to: '/sponsor/command-center?tab=dashboard', replace: true },
  { from: '/enterprise-analytics', to: '/sponsor/command-center?tab=dashboard', replace: true },
  { from: '/phase6-features', to: '/admin/command-center', replace: true },
  { from: '/professional-dashboard', to: '/professional', replace: true },
  
  // Trial sites redirects
  { from: '/trial-sites/tanzania', to: '/trial-sites/africa', replace: true },
  { from: '/trial-sites/uganda', to: '/trial-sites/africa', replace: true },
  
  // Legacy pricing redirects
  { from: '/pricing/professionals', to: '/demo/professionals', replace: true },
  { from: '/pricing/sponsors', to: '/demo/sponsors', replace: true },
  { from: '/pricing/advocacy', to: '/demo/advocacy', replace: true },
  
  // Subscription redirects
  { from: '/subscription', to: '/demo-booking', replace: true },
  { from: '/manage-subscription', to: '/demo-booking', replace: true },
  
  // Referral route redirects
  { from: '/referral-intake', to: '/referrals/intake', replace: true },
  { from: '/site-referral-review', to: '/referrals/review', replace: true },
  
  // Legacy vanity URLs removed - Ashmed redirect deprecated per user request
  
  // Trial search redirect (legacy URL)
  { from: '/find-trials', to: '/trials', replace: true },
  
  // Press page redirect to news
  { from: '/press', to: '/news', replace: true },
  
  // Onboarding typo redirects
  { from: '/onboarding-trial-site', to: '/onboard-trial-site', replace: true },
  { from: '/onboarding-cro', to: '/onboard-cro', replace: true },
  
  // Regulatory module embedding redirects
  // /gov/who-gbt-dashboard now handled by umojareg.redirects.ts (cross-origin to UmojaReg)
  { from: '/ethics-track', to: '/site-dashboard', replace: true },
  { from: '/twinnet', to: '/site-dashboard', replace: true },

  // Gov surveillance re-home — primary lives at /gov/:country/*.
  // Old aliases -> Kenya-scoped default (existing /admin/* super-admin mirrors stay live for governance-only view).
  { from: '/admin/kenya-idsr-matrix/edit', to: '/gov/KE/idsr-matrix', replace: true },
  { from: '/admin/idsr-submissions/new', to: '/gov/KE/idsr-submissions', replace: true },
  { from: '/admin/me-indicators/record', to: '/gov/KE/me-indicators', replace: true },
  { from: '/admin/lims-idsr-linkages/link', to: '/gov/KE/lims-idsr-linkages', replace: true },
];

/**
 * Get redirect target for a given path
 */
export function getRedirectTarget(path: string): string | null {
  const redirect = redirects.find(r => r.from === path);
  return redirect ? redirect.to : null;
}

/**
 * Check if a path should be redirected
 */
export function shouldRedirect(path: string): boolean {
  return redirects.some(r => r.from === path);
}
